import React, { memo, useCallback, useState, useEffect } from 'react';
import { View, Text } from 'react-native';
import { THEME } from '../../constants/styles';
import { EMPLOYER_TYPE_MED_RECRUITER } from '../../constants/employerTypes';
import { REQUESTING, SUCCESS } from '../../actions/statusTypes';
import { Container, NavBar, FormFooter } from '../components';

import { useSelector, useDispatch } from 'react-redux';

import { updateEmployer, createEmployer, resetState, resetSaveStatus } from './slice';
import { uploadFilesAndSaveToServer, resetState as resetUploadState } from '../components/actions';
import { EMPLOYER_LOGO } from '../../constants/objectTypes';

import NameForm from './name_form';
import AdminForm from './admin_form';
import LocationForm from './location_form';
import EventTypesForm from './event_types_form';
import ConfigForm from './config_form';
import LogoForm from './logo_form';

const FORMS_CREATE = [NameForm, AdminForm, LocationForm, EventTypesForm, ConfigForm];
const FORMS_EDIT = [LogoForm, NameForm, LocationForm, EventTypesForm, ConfigForm];

const FORMS_CREATE_MED_RECRUITER = [NameForm, AdminForm, LocationForm, ConfigForm];
const FORMS_EDIT_MED_RECRUITER = [LogoForm, NameForm, LocationForm, ConfigForm];

const AddEditForm = memo((props) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.screens.baseConfig?.saveEmployerStatus);
  const uploadStatus = useSelector((state) => state.screens.components?.uploads?.status);
  const uploadError = useSelector((state) => state.screens.components?.uploads?.errorMessage);
  const error = useSelector((state) => state.screens.baseConfig?.saveEmployerError);

  useEffect(NavBar.setup(props), []);
  useEffect(() => {
    return () => {
      dispatch(resetState());
      dispatch(resetUploadState());
    };
  }, []);

  const creatingNewEmployer = !props.employer;
  const [step, setStep] = useState(props.activeStep || 1);
  const [employerData, setEmployerData] = useState(props.employer || {});
  const [tmpEmployerData, setTmpEmployerData] = useState(props.employer || {});
  const [isValid, setIsValid] = useState(false);
  const [confirmationOpts, setConfirmationOpts] = useState(null);
  const [imageData, setImageData] = useState(null);
  const employerType = employerData?.uiConfig?.type || employerData?.type;

  const formSet = creatingNewEmployer
    ? employerType === EMPLOYER_TYPE_MED_RECRUITER
      ? FORMS_CREATE_MED_RECRUITER
      : FORMS_CREATE
    : employerType === EMPLOYER_TYPE_MED_RECRUITER
      ? FORMS_EDIT_MED_RECRUITER
      : FORMS_EDIT;

  const handleDataChange = useCallback((data, isValid) => {
    if (creatingNewEmployer) {
      setEmployerData(data);
    } else {
      setTmpEmployerData(data);
    }
    setIsValid(isValid);
  }, []);

  const handleImageDataChange = useCallback((imageData) => {
    setImageData(imageData);
    setIsValid(true);
  }, []);

  const doSave = useCallback(() => {
    setConfirmationOpts(null);
    dispatch(updateEmployer({ employer: tmpEmployerData }));
  }, [tmpEmployerData]);

  const doCreate = useCallback(() => {
    setConfirmationOpts(null);
    dispatch(createEmployer({ employer: employerData }));
  }, [employerData]);

  const uploadLogo = useCallback(() => {
    dispatch(
      uploadFilesAndSaveToServer(EMPLOYER_LOGO, props.employer?.employerId, [imageData], true, true)
    );
  }, [imageData]);

  const submit = useCallback(() => {
    if (creatingNewEmployer) {
      doCreate();
      return;
    }

    let needVerification = employerData.slug !== tmpEmployerData.slug && 'SLUG';
    needVerification =
      needVerification || (employerData.timeZone !== tmpEmployerData.timeZone && 'TIME ZONE');

    if (needVerification) {
      setConfirmationOpts({
        title: 'Are you sure?',
        description: `Modifying the ${needVerification} will break things.`,
        confirm: `Yes, modify the ${needVerification}`,
        onConfirmed: doSave,
        onCancelled: () => {
          setConfirmationOpts(null);
        },
      });
    } else {
      doSave();
    }
  }, [tmpEmployerData, employerData, doSave, doCreate]);

  const back = useCallback(() => {
    setStep(Math.max(1, step - 1));
    setIsValid(false);
    dispatch(resetSaveStatus());
    dispatch(resetUploadState());
  }, [step]);

  const next = useCallback(() => {
    setStep(Math.min(formSet.length, step + 1));
    setIsValid(false);
    dispatch(resetSaveStatus());
    dispatch(resetUploadState());
  }, [step]);

  // if we're editing an existing employer then we only want to update the data
  // that is passed among forms if/when the user saves the screen
  useEffect(() => {
    if (!creatingNewEmployer && status === SUCCESS) {
      setEmployerData(tmpEmployerData);
    }
  }, [status]);

  // if we're creating a new employer then we want to update the data to whatever the user
  // enteres into each form as we save it all at the end
  useEffect(() => {
    if (creatingNewEmployer) {
      setEmployerData(tmpEmployerData);
    }
  }, [tmpEmployerData]);

  const FormComponent = formSet[step - 1];
  const showSave = (!creatingNewEmployer || step === formSet.length) && !props.readonly;
  const submitAction = FormComponent === LogoForm ? uploadLogo : submit;

  function renderFooter() {
    return (
      <FormFooter
        saveLabel="Save"
        cancelLabel="Back"
        nextLabel="Next"
        onSave={showSave ? submitAction : null}
        onCancel={step > 1 ? back : null}
        onNext={step < formSet.length ? next : null}
        saveDisabled={!isValid || status === REQUESTING}
        nextDisabled={!isValid && creatingNewEmployer}
        cancelDisabled={status === REQUESTING}
      />
    );
  }

  function renderForm() {
    if (!FormComponent) return null;
    return (
      <FormComponent
        employer={employerData}
        readOnly={!!props.readonly}
        onDataChange={handleDataChange}
        onImageDataChange={handleImageDataChange}
      />
    );
  }

  return (
    <Container
      scrollView={true}
      stickyFooter={renderFooter()}
      successMessage={
        status === SUCCESS
          ? 'Employer saved successfully!'
          : uploadStatus === SUCCESS
            ? 'Logo uploaded successfully!'
            : null
      }
      activityIndicator={status === REQUESTING || uploadStatus === REQUESTING}
      errorMessage={error?.message || uploadError?.message || null}
      confirmationOpts={confirmationOpts}
    >
      <View style={THEME.inputsContainer}>{renderForm()}</View>
    </Container>
  );
});

export default AddEditForm;
